import { useRef } from "react";
import { toast } from "sonner";
import { Check, Download, Mail, Wallet, Lock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SiteHeader, PageFooter } from "@/components/brand/Brand";
import { CredentialCard, ParticipantActivity } from "@/components/credencial/CredentialCard";
import { useDemo } from "@/lib/demo/store";
import { fullName, qrValue, downloadBlob } from "@/lib/demo/utils";
import { useI18n } from "@/lib/i18n/i18n";
import { Link } from "react-router-dom";

function saveCredentialImage(qrBox, p) {
  const qr = qrBox?.querySelector("canvas");
  if (!qr) return;
  const c = document.createElement("canvas");
  c.width = 720; c.height = 1040;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, 720, 1040);
  ctx.fillStyle = "#8B0000"; ctx.fillRect(0, 0, 720, 110);
  ctx.fillStyle = "#fff"; ctx.font = "bold 34px Outfit, sans-serif"; ctx.fillText("FORO DE EMPLEO UPV 2027", 48, 70);
  ctx.fillStyle = "#0F172A"; ctx.font = "bold 40px Outfit, sans-serif"; ctx.fillText(fullName(p).toUpperCase(), 48, 180);
  ctx.fillStyle = "#475569"; ctx.font = "26px sans-serif"; ctx.fillText(`${p.titulacion} · ${p.centro}`, 48, 225);
  ctx.drawImage(qr, 110, 270, 500, 500);
  ctx.fillStyle = "#0F172A"; ctx.font = "bold 36px monospace"; ctx.textAlign = "center"; ctx.fillText(p.token, 360, 840);
  ctx.font = "bold 26px monospace"; ctx.fillText("12–13 MAYO 2027", 360, 900);
  ctx.fillStyle = "#94A3B8"; ctx.font = "20px sans-serif"; ctx.fillText("Technology by Do IT Happen", 360, 1000);
  c.toBlob((b) => b && downloadBlob(b, `Credencial_${p.token}.png`));
}

export default function Credencial() {
  const { state, activeParticipant: p, setActiveParticipant } = useDemo();
  const qrRef = useRef(null);
  const { t } = useI18n();

  const save = () => { saveCredentialImage(qrRef.current, p); toast.success(t("Credencial guardada"), { description: t("Imagen PNG descargada en tu dispositivo.") }); };
  const email = () => toast.success(t("Credencial enviada a {email}", { email: p.email }), { description: t("Simulación: en la demo no se envían emails reales.") });
  const wallet = () => toast(t("Añadir a Wallet"), { description: t("Disponible en la versión final (Apple Wallet / Google Wallet).") });

  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="credencial-page">
      <SiteHeader right={
        <Select value={p.id} onValueChange={setActiveParticipant}>
          <SelectTrigger className="h-9 w-[180px] rounded-full text-xs sm:w-[220px]" data-testid="credencial-participant-select"><SelectValue /></SelectTrigger>
          <SelectContent>{state.participants.map((x) => <SelectItem key={x.id} value={x.id}>{fullName(x)}</SelectItem>)}</SelectContent>
        </Select>
      } />
      <main className="mx-auto max-w-6xl px-5 py-12 lg:px-10 lg:py-16">
        <div className="fade-up mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Registro completado")}</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl" data-testid="credencial-greeting">{t("¡TODO LISTO, {name}!", { name: p.firstName.toUpperCase() })}</h1>
          <p className="mt-3 text-lg text-slate-600">{t("Tu acceso al Foro de Empleo UPV 2027")}</p>
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-10 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)] lg:gap-14">
          <div className="fade-up-1 mx-auto w-full min-w-0 max-w-[440px]"><CredentialCard ref={qrRef} participant={p} /></div>
          <div className="fade-up-2 space-y-6">
            <p className="font-display text-2xl font-semibold leading-snug tracking-tight sm:text-3xl" data-testid="credencial-message">{t("“Este código es tu identificación durante el Foro.”")}</p>
            <div className="grid gap-3 sm:grid-cols-3">
              {["Acceso al recinto", "Compartir CV", "Interacción con empresas"].map((l, i) => (
                <div key={l} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`credencial-indicator-${i + 1}`}>
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><Check className="h-4 w-4" strokeWidth={3} /></span>
                  <span className="text-sm font-semibold">{t(l)}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <button onClick={save} className="flex h-14 flex-1 items-center justify-center gap-2 rounded-2xl bg-upv font-display font-bold tracking-wider text-white transition-colors hover:bg-upv-dark" data-testid="credencial-save-button"><Download className="h-4 w-4" /> {t("GUARDAR CREDENCIAL")}</button>
              <button onClick={email} className="flex h-14 flex-1 items-center justify-center gap-2 rounded-2xl border-2 border-slate-900 font-display font-bold tracking-wider transition-colors hover:bg-slate-900 hover:text-white" data-testid="credencial-email-button"><Mail className="h-4 w-4" /> {t("ENVIAR POR EMAIL")}</button>
            </div>
            <button onClick={wallet} className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 text-sm font-semibold text-white transition-opacity hover:opacity-90 sm:w-auto sm:px-6" data-testid="credencial-wallet-button"><Wallet className="h-4 w-4" /> {t("Añadir a Wallet")}</button>
            <div className="rounded-[28px] border border-slate-200 bg-white p-6" data-testid="credencial-privacy">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-400"><Lock className="h-3.5 w-3.5" /> {t("Qué contiene tu QR")}</div>
              <p className="mt-3 break-all font-mono text-sm font-semibold text-slate-900" data-testid="credencial-qr-value">{qrValue(p.token)}</p>
              <p className="mt-2 text-sm text-slate-500">{t("Solo un token seguro. Tu nombre, email, teléfono y CV nunca viajan dentro del código: la plataforma los asocia de forma protegida.")}</p>
            </div>
            <Link to="/empresas" className="flex h-12 items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 text-sm font-semibold text-slate-700 transition-colors hover:border-upv hover:text-upv" data-testid="credencial-companies-link">{t("Ver empresas participantes")} →</Link>
            <ParticipantActivity state={state} participant={p} />
          </div>
        </div>
      </main>
      <PageFooter />
    </div>
  );
}
